// OWNED BY the calls implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/calls/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// שיחות tab (mock s24): all calls with this contact across the user's (and,
// for org admins, the team's) virtual numbers — filter pills with counts,
// timeline rows with AI summaries, slim audio player + transcript, task-from-
// call, and call-back on missed calls. Data layer + legacy-logic ports live
// under ./calls/ (callsData.ts).

import { useEffect, useMemo, useState } from "react";
import { Stack, Chip, Button, Skeleton, Typography, Box } from "@material-ui/core";
import { useSnackbar } from "notistack5";
import EmptyContent from "components/EmptyContent";
import { TabCard } from "../TabCard";
import { getCached, subscribeCached } from "../data/sessionCache";
import { useContactCalls } from "./calls/useContactCalls";
import { ContactCallRow } from "./calls/ContactCallRow";
import {
  contactCallsKey,
  countCallsByKind,
  filterCallsByKind,
  preloadContactCalls,
  getCallsHeaderCaption
} from "./calls/callsData";
import type { CallsFilterId } from "./calls/callsData";
import type { CallSummary, RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

const FILTER_PILLS: ReadonlyArray<{ id: CallsFilterId; label: string }> = [
  { id: "all", label: "הכל" },
  { id: "incoming", label: "נכנסות" },
  { id: "outgoing", label: "יוצאות" },
  { id: "missed", label: "לא נענו" }
];

const PAGE_SIZE = 10;

export default function CallsTab({ contact }: ContactTabComponentProps): JSX.Element {
  const { calls, error, refresh } = useContactCalls(contact);

  const [activeFilter, setActiveFilter] = useState<CallsFilterId>("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [playingCallId, setPlayingCallId] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (error && calls === undefined) {
      enqueueSnackbar("טעינת השיחות נכשלה", { variant: "error", autoHideDuration: 4000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const allCalls = useMemo(() => calls ?? [], [calls]);
  const counts = useMemo(() => countCallsByKind(allCalls), [allCalls]);
  const filteredCalls = useMemo(
    () => filterCallsByKind(allCalls, activeFilter),
    [allCalls, activeFilter]
  );
  const visibleCalls = filteredCalls.slice(0, visibleCount);
  const hiddenCount = filteredCalls.length - visibleCalls.length;

  const handleFilterChange = (filterId: CallsFilterId): void => {
    setActiveFilter(filterId);
    setVisibleCount(PAGE_SIZE);
  };

  // undefined data = the list was never (successfully) fetched. On a truly
  // cold mount the cache entry doesn't even exist yet on first render (the
  // fetch starts in the hook's effect and only notifies when it settles), so
  // the inflight `loading` flag alone is false during the whole initial fetch
  // — key off the data instead, and never present that as "אין עדיין שיחות".
  const isColdStart = calls === undefined && !error;
  const isLoadFailed = calls === undefined && !!error;
  const isEmpty = calls !== undefined && allCalls.length === 0;

  return (
    <TabCard sx={{ width: "100%", maxWidth: 880, mx: "auto" }}>
      {/* header */}
      <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
        <Typography variant="h6">שיחות</Typography>
        {allCalls.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {getCallsHeaderCaption(allCalls)}
          </Typography>
        )}
      </Stack>

      {/* filter pills */}
      {allCalls.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5, mb: 3 }}>
          {FILTER_PILLS.map(({ id, label }) => {
            const isActive = id === activeFilter;
            return (
              <Chip
                key={id}
                clickable
                label={`${label} · ${counts[id]}`}
                variant={isActive ? "filled" : "outlined"}
                onClick={() => handleFilterChange(id)}
                sx={
                  isActive
                    ? {
                        bgcolor: "grey.800",
                        color: "common.white",
                        "&:hover": { bgcolor: "grey.700" },
                        "&:focus": { bgcolor: "grey.800" }
                      }
                    : undefined
                }
              />
            );
          })}
        </Stack>
      )}

      {isColdStart ? (
        <CallsListSkeleton />
      ) : isLoadFailed ? (
        <Stack alignItems="center" gap={1.5} sx={{ py: 5 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            טעינת השיחות נכשלה
          </Typography>
          <Button variant="outlined" color="inherit" onClick={refresh}>
            ניסיון חוזר
          </Button>
        </Stack>
      ) : isEmpty ? (
        <EmptyContent
          title="אין עדיין שיחות"
          description="שיחות הטלפון עם הלקוח — כולל הקלטות, תמלולים וסיכומי AI — יופיעו כאן."
        />
      ) : filteredCalls.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
          אין שיחות מהסוג הזה
        </Typography>
      ) : (
        <>
          {visibleCalls.map((call, index) => (
            <ContactCallRow
              key={`${call.virtualNumber}|${call.id}`}
              contact={contact}
              call={call}
              isLast={index === visibleCalls.length - 1}
              playingCallId={playingCallId}
              onPlayingCallIdChange={setPlayingCallId}
            />
          ))}
          {hiddenCount > 0 && (
            <Button
              fullWidth
              color="inherit"
              sx={{ mt: 2, color: "text.secondary" }}
              onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)}
            >
              {`הצגת עוד שיחות (${hiddenCount})`}
            </Button>
          )}
        </>
      )}
    </TabCard>
  );
}

function CallsListSkeleton(): JSX.Element {
  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      {[0, 1, 2].map((index) => (
        <Stack key={index} direction="row" gap={1.5} alignItems="flex-start">
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="35%" />
            <Skeleton variant="text" width="70%" />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

// Badge for the tab strip: total calls count, read ONLY from the session cache
// (warmed by preload / kept live by the tab's listeners) — never fetches.
export function useCallsTabBadge(contact: RealEstateContact): TabBadge {
  const contactId = contact?.id ?? "";
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!contactId) return undefined;
    return subscribeCached(contactCallsKey(contactId), () => forceRender((n) => n + 1));
  }, [contactId]);

  const cachedCalls = contactId ? getCached<CallSummary[]>(contactCallsKey(contactId)) : undefined;
  if (!cachedCalls || cachedCalls.length === 0) return null;
  return { count: cachedCalls.length };
}

// Cache warm-up fired on contacts-table row hover and page mount. Idempotent
// and cheap: deduped through fetchCached (stale-while-revalidate).
export function preloadCallsTab(contact: RealEstateContact): void {
  preloadContactCalls(contact);
}
